/**
 * SOLID Review
 * S — Good. One concern: the session's shells — the use cases that start a session, enter the game
 *     screen and move through the funnel, each as load → decide → save around at most one server call.
 *     No rule lives here; the Domain's decisions hold them all.
 * O — Good. A new way into the game is one more branch of `proceed` and one decision.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the session's ops of `ClientUseCases`.
 * D — Strong. Seven port interfaces, the account's sign-out and the shared steps, all injected; no adapter,
 *     no store, no React.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IEntityClientResume, IEntityClientStored } from '../../domain/ports/schemas/ClientSession';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  ICommandStartSession, ICommandCreateGuest, ICommandTableCode, ICommandNavigate, ICommandSessionStarted, ICommandPlanEntry,
  ICommandMeAnswered, ICommandResumeValidated, ICommandPlanRestore, ICommandRestoreJoined, ICommandRoundRestored,
  ICommandPlanIntent, ICommandJoinRouted, ICommandTableJoined, ICommandTableRejoined, ICommandTableLateJoined,
  ICommandTableHosted, ICommandActiveRoomFound, ICommandGuestCreated, ICommandTableCodeChecked, IEntityEntryPlan,
  IPayloadSeat,
} from '../../domain/ports/schemas/SessionMoves';
import type { IPayloadRoomRead } from '../../domain/ports/schemas/TableMoves';
import {
  SERVER_REFUSED,
  type IPortClientTabStorage, type IPortClientRoomServer, type IPortClientRoundServer, type IPortClientPlayerServer,
  type IPortClientAuthServer, type IPayloadServerRoomCode,
} from '../ports/contracts';
import { DONE, outcomeOf, type ClientSteps, type TOutcome } from '../shared/ClientSteps';
import type { IServiceSignOut } from '../auth/AuthContext';

export interface IConfigDepSessionContext {
  readonly decisions: IPortClientDecisions;
  readonly tabStorage: IPortClientTabStorage;
  readonly rooms: IPortClientRoomServer;
  readonly rounds: IPortClientRoundServer;
  readonly players: IPortClientPlayerServer;
  readonly auth: IPortClientAuthServer;
  /** The account's sign-out (FE3), shared with the Log out buttons. */
  readonly account: IServiceSignOut;
  /** The one serial section, shared with the table. */
  readonly steps: ClientSteps;
}

/** How identity settled: known (or about to be named), signed out, or `me` could not be asked. */
type TIdentified = 'known' | 'signedOut' | 'unreachable';

const NOTHING: ICommandNothing = {};
/** Storage the tab cannot read holds nothing, as the storage helpers answered null on any error. */
const NOTHING_STORED: IEntityClientStored = { guest: null, resume: null };

/**
 * THE SESSION'S SHELLS (frontend strict-ports plan, FE2, 2026-10-03). What were four hooks watching the
 * store from AppRoot — identity bootstrap, session recovery, cold-login rejoin, smart-join routing — plus
 * MainMenuPanel's auto-host and redirect, IdentityModal's submit and EnterCodeScreen's check, are shells
 * of FE1's pattern on the shared serial section: a step is snapshot → decide → save (→ keep), and a
 * server call sits between two steps.
 *
 * ENTERING THE GAME. One run per mount of the game screen (a second is refused while one is in flight):
 * identity and the stored game side by side — as the hooks ran — then, unless a seat was taken, what the
 * session intends: the code, the cold rejoin, the host, or back to the welcome screen. The order of that
 * last part is the hooks' guards; that the stored game goes before a code, rather than racing it, is new
 * (deviation, flagged in §28.199).
 *
 * Every server read that fails reads as "nothing", exactly as the hooks' port bindings mapped them:
 * a failed lookup is no room, a failed restore join is a game that is over, a failed round read is a
 * table between rounds.
 */
export class SessionContext {
  constructor(private readonly deps: IConfigDepSessionContext) {}

  private async stored(ctx: IContextRequest): Promise<IEntityClientStored> {
    const read = await this.deps.tabStorage.stored(ctx);
    return read.success ? read.value : NOTHING_STORED;
  }

  /** The page loaded: who the stored token belongs to (hydrate's JWT half, FE3), the stored records, the URL. */
  async startSession(ctx: IContextRequest, command: ICommandStartSession): Promise<TOutcome> {
    const token = await this.deps.auth.account(ctx);
    const started: ICommandSessionStarted = { intent: command, stored: await this.stored(ctx), token: token.success ? token.value : null };
    return outcomeOf(await this.deps.steps.session(ctx, (input) => this.deps.decisions.sessionStarted(ctx, input, started)));
  }

  /** The game screen mounted. */
  async enterGame(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    const { decisions, steps } = this.deps;
    const entry: ICommandPlanEntry = { stored: await this.stored(ctx) };
    const plan = await steps.session(ctx, (input) => decisions.planEntry(ctx, input, entry));
    if (!plan.success) return plan;
    try {
      const [identified] = await Promise.all([this.identify(ctx, plan.value.identity), this.resumeStored(ctx, plan.value)]);
      if (identified === 'unreachable') {
        await steps.session(ctx, (input) => decisions.meUnreachable(ctx, input, NOTHING));
      } else if (identified === 'known') {
        await this.proceed(ctx, false);
      }
      return DONE;
    } finally {
      await steps.session(ctx, (input) => decisions.entryFinished(ctx, input, NOTHING));
    }
  }

  /**
   * Who the tab is: nothing to do for a known player or a visitor about to name themselves. A token asks
   * `me`: no player behind it signs the tab out (FE3: through the account's sign-out, which forgets the
   * stored game too); a server that could not be asked leaves the account alone and stops the entry —
   * until FE3 it read as "no player" and signed a registered player out (§28.199).
   */
  private async identify(ctx: IContextRequest, identity: IEntityEntryPlan['identity']): Promise<TIdentified> {
    if (identity !== 'me') return 'known';
    const { decisions, steps, auth } = this.deps;
    const me = await auth.me(ctx);
    if (!me.success) return 'unreachable';
    const answered: ICommandMeAnswered = { me: me.value };
    const decided = await steps.session(ctx, (input) => decisions.meAnswered(ctx, input, answered));
    if (!decided.success || !decided.value.signOut) return 'known';
    await this.deps.account.signOut(ctx, NOTHING);
    return 'signedOut';
  }

  /** The stored game: checked by its code when it is this player's, then re-joined. */
  private async resumeStored(ctx: IContextRequest, plan: IEntityEntryPlan): Promise<void> {
    const { record } = plan;
    if (plan.resume === 'none' || record === null) return;
    if (plan.resume === 'validate') {
      const code: IPayloadServerRoomCode = { roomCode: record.roomCode };
      const found = await this.deps.rooms.getRoomByCode(ctx, code);
      const lookup: ICommandResumeValidated['lookup'] = found.success && found.value !== null ? { roomStatus: found.value.roomStatus } : null;
      const validated: ICommandResumeValidated = { record, lookup };
      const checked = await this.deps.steps.session(ctx, (input) => this.deps.decisions.resumeValidated(ctx, input, validated));
      if (!checked.success || !checked.value.proceed) return;
    }
    await this.restore(ctx, record);
  }

  /** Back into a stored game: re-join its room as the record's player, then its live round. */
  private async restore(ctx: IContextRequest, record: IEntityClientResume): Promise<void> {
    const { decisions, steps, rooms } = this.deps;
    const restoring: ICommandPlanRestore = { record };
    const plan = await steps.session(ctx, (input) => decisions.planRestore(ctx, input, restoring));
    if (!plan.success) return;
    const joined = await rooms.joinRoom(ctx, plan.value);
    const answer: ICommandRestoreJoined = { record, room: joined.success ? joined.value : null };
    const seated = await steps.session(ctx, (input) => decisions.restoreJoined(ctx, input, answer));
    if (seated.success && seated.value.read !== null) await this.restoreRound(ctx, seated.value.read);
  }

  /** The live round after a mid-game seat. */
  private async restoreRound(ctx: IContextRequest, request: IPayloadSeat): Promise<void> {
    const read: IPayloadRoomRead = { roomId: request.roomId };
    const round = await this.deps.rounds.getActiveRoundForRestore(ctx, read);
    const restored: ICommandRoundRestored = { request, activeRound: round.success ? round.value : null };
    await this.deps.steps.session(ctx, (input) => this.deps.decisions.roundRestored(ctx, input, restored));
  }

  /** What the session intends once identity and the stored game have settled. */
  private async proceed(ctx: IContextRequest, coldChecked: boolean): Promise<void> {
    const planned: ICommandPlanIntent = { coldChecked };
    const intent = await this.deps.steps.session(ctx, (input) => this.deps.decisions.planIntent(ctx, input, planned));
    if (!intent.success) return;
    switch (intent.value.next) {
      case 'join':
        // Only a join that SETTLED moves the entry on. The code stays pending until a decision consumes it,
        // so after a failed step the intent would be `join` again — and was, until the request's deadline
        // (frontend strict-ports FE9, §28.206: a tight loop with no I/O froze the tab; with the lookup in
        // it, it hammered the server).
        if (!(await this.joinByCode(ctx))) return;
        return this.proceed(ctx, true);
      case 'cold':
        await this.coldRejoin(ctx);
        return this.proceed(ctx, true);
      case 'host':
        return this.host(ctx);
      default:
        // seated; idle (the decision sent the session back to the welcome screen); or waiting for a name.
        return;
    }
  }

  /**
   * The pending code: look it up, route it, take the seat. Answers whether the join SETTLED — the routing
   * decision and the seat's ran — so the caller does not ask again for a code nothing has consumed.
   */
  private async joinByCode(ctx: IContextRequest): Promise<boolean> {
    const { decisions, steps, rooms } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planJoin(ctx, input, NOTHING));
    if (!plan.success || plan.value.code === null) return false;
    const code = plan.value.code;
    const roomCode: IPayloadServerRoomCode = { roomCode: code };
    const found = await rooms.getRoomByCode(ctx, roomCode);
    const looked: ICommandJoinRouted = { code, lookup: found.success ? found.value : null, failed: !found.success };
    const routed = await steps.session(ctx, (input) => decisions.joinRouted(ctx, input, looked));
    if (!routed.success) return false;
    const route = routed.value.route;
    if (route.type === 'JOIN') {
      const joined = await rooms.joinRoom(ctx, route.request);
      const answer: ICommandTableJoined = { request: route.request, room: joined.success ? joined.value : null };
      return (await steps.session(ctx, (input) => decisions.tableJoined(ctx, input, answer))).success;
    }
    if (route.type === 'REJOIN') {
      const rejoined = await rooms.rejoinRoomByCode(ctx, route.request);
      const answer: ICommandTableRejoined = { request: route.request, rejoin: rejoined.success ? rejoined.value : null };
      const seated = await steps.session(ctx, (input) => decisions.tableRejoined(ctx, input, answer));
      if (seated.success && seated.value.read !== null) await this.restoreRound(ctx, seated.value.read);
      return seated.success;
    }
    if (route.type === 'LATE_JOIN') {
      const joined = await rooms.lateJoinRoom(ctx, route.request);
      const answer: ICommandTableLateJoined = { request: route.request, room: joined.success ? joined.value : null };
      const seated = await steps.session(ctx, (input) => decisions.tableLateJoined(ctx, input, answer));
      if (seated.success && seated.value.read !== null) await this.restoreRound(ctx, seated.value.read);
      return seated.success;
    }
    // Stopped — no such room, a full one, an unreachable server: the routing decision consumed the code.
    return true;
  }

  /** A registered player with nothing stored: does the server still seat them somewhere? */
  private async coldRejoin(ctx: IContextRequest): Promise<void> {
    const { decisions, steps, rooms } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planColdRejoin(ctx, input, NOTHING));
    if (!plan.success) return;
    const found = await rooms.getActiveRoomForPlayer(ctx, plan.value);
    const answer: ICommandActiveRoomFound = { request: plan.value, room: found.success ? found.value : null };
    const decided = await steps.session(ctx, (input) => decisions.activeRoomFound(ctx, input, answer));
    if (decided.success && decided.value.record !== null) await this.restore(ctx, decided.value.record);
  }

  /** The funnel's host intent: create the table. A refusal keeps the server's own words for the panel. */
  private async host(ctx: IContextRequest): Promise<void> {
    const { decisions, steps, rooms } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planHost(ctx, input, NOTHING));
    if (!plan.success) return;
    const created = await rooms.createRoom(ctx, plan.value);
    const answer: ICommandTableHosted = created.success
      ? { request: plan.value, room: created.value, serverMessage: null }
      : { request: plan.value, room: null, serverMessage: created.error.code === SERVER_REFUSED ? created.error.message : null };
    await steps.session(ctx, (input) => decisions.tableHosted(ctx, input, answer));
  }

  /**
   * The name modal's submit: create the guest, seat the tab as them, then go on with the entry. An
   * answer that arrives after the caller's deadline is refused by the decisions port's guard
   * (`DEADLINE`), so a modal that stopped waiting is not identified behind its back.
   */
  async createGuest(ctx: IContextRequest, command: ICommandCreateGuest): Promise<TOutcome> {
    const { decisions, steps, players } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planGuest(ctx, input, command));
    if (!plan.success) return plan;
    const created = await players.createPlayer(ctx, plan.value);
    if (!created.success) return created;
    const answer: ICommandGuestCreated = { request: plan.value, player: created.value };
    const seated = await steps.session(ctx, (input) => decisions.guestCreated(ctx, input, answer));
    if (!seated.success) return seated;
    await this.proceed(ctx, false);
    return DONE;
  }

  /** EnterCodeScreen's Continue: no code hosts; a code must exist before the game screen opens. */
  async checkTableCode(ctx: IContextRequest, command: ICommandTableCode): Promise<TOutcome> {
    const { decisions, steps, rooms } = this.deps;
    const plan = await steps.session(ctx, (input) => decisions.planTableCode(ctx, input, command));
    if (!plan.success) return plan;
    const code = plan.value.code;
    if (code === null) return DONE;
    const roomCode: IPayloadServerRoomCode = { roomCode: code };
    const found = await rooms.getRoomByCode(ctx, roomCode);
    if (!found.success) return found;
    const checked: ICommandTableCodeChecked = { code, found: found.value !== null };
    return outcomeOf(await steps.session(ctx, (input) => decisions.tableCodeChecked(ctx, input, checked)));
  }

  async navigate(ctx: IContextRequest, command: ICommandNavigate): Promise<TOutcome> {
    return outcomeOf(await this.deps.steps.session(ctx, (input) => this.deps.decisions.navigate(ctx, input, command)));
  }

  async dismissJoinError(ctx: IContextRequest, _command: ICommandNothing): Promise<TOutcome> {
    return outcomeOf(await this.deps.steps.session(ctx, (input) => this.deps.decisions.dismissJoinError(ctx, input, NOTHING)));
  }
}
